import { faSave } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, CircularProgress, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { deepEquals } from "app/tools/common"
import { INotficationSetting, INotificationSettings, INotificationType } from "notification/model"
import { cancelSettings, loadNotificationSettings, updateSettings } from "notification/tools/common"
import { PageLoading } from "page/components/PageLoading"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { NotificationSetting } from "./NotificationSetting"

interface INotificationPageProps {
  settings: INotificationSettings
  initialSettings: INotificationSettings
  isLoading: boolean
  hasLoadingError: boolean
  isUpdating: boolean
}

const NotificationPageFn = React.memo(
  ({ settings, initialSettings, hasLoadingError, isLoading, isUpdating }: INotificationPageProps): JSX.Element => {
    // triggered on first render only
    useEffect(() => {
      loadNotificationSettings()
    }, [])
    const hasChange = !deepEquals(initialSettings, settings)
    const keys: Array<keyof INotificationSettings> = (settings ? Object.keys(settings) : []) as Array<
      keyof INotificationSettings
    >
    if (!IS_NOT_PROD) {
      return <Typography color="textPrimary">{translate("notification.not.finish")}</Typography>
    }
    return (
      <FlexBox jc="start" ai="start" fullWidth fullHeight vertical>
        {hasLoadingError ? (
          <UnexpectedErrorPage key="notification.settings.load.error" />
        ) : isLoading || settings === null ? (
          <PageLoading title={translate("label.loading.data")} sx={{ mt: "2rem", mb: "2rem" }} />
        ) : (
          <FlexBox jc="start" ai="start" fullWidth vertical gap sx={{ p: "1rem", height: "100%", overflow: "auto" }}>
            {keys.map(category => (
              <FlexBox jc="start" ai="start" vertical fullWidth key={category}>
                <FlexBox fullWidth ai="start" jc="space-between" sx={{ mb: ".5rem" }}>
                  <Typography id={`notification-settings-${category}-title`} color="textPrimary" fontWeight={500}>
                    {translate(`notification.category.${category}`)}
                  </Typography>
                  <FlexBox sx={{ minWidth: "8rem" }}>
                    <Typography color="textPrimary" sx={{ mr: "1rem" }}>
                      email
                    </Typography>
                    <Typography color="textPrimary" sx={{ mr: ".5rem" }}>
                      push
                    </Typography>
                  </FlexBox>
                </FlexBox>
                {Object.keys(settings[category]).map(notificationType => {
                  const values = (settings[category] as any)[notificationType] as INotficationSetting
                  return (
                    <NotificationSetting
                      category={category}
                      type={notificationType as INotificationType}
                      value={values}
                      disabled={isUpdating}
                    />
                  )
                })}
              </FlexBox>
            ))}
            <FlexBox fullWidth gap jc="end" sx={{ mt: "1rem" }}>
              <Button
                id={"notification-settings-button-cancel"}
                variant="outlined"
                disabled={!hasChange || isUpdating}
                onClick={() => cancelSettings()}>
                {translate("button.cancel")}
              </Button>
              <Button
                id={"notification-settings-save-cancel"}
                variant="contained"
                disabled={!hasChange || isUpdating}
                onClick={() => updateSettings(settings)}
                startIcon={isUpdating ? <CircularProgress size="1.5rem" /> : <FontAwesomeIcon icon={faSave} />}>
                {translate("button.save")}
              </Button>
            </FlexBox>
          </FlexBox>
        )}
      </FlexBox>
    )
  }
)

export const NotificationPage = connect(
  (state: IRootState): INotificationPageProps => ({
    settings: state.notification.clientSettings,
    initialSettings: state.notification.serverSettings,
    isLoading: state.notification.isLoadingSettings,
    hasLoadingError: state.notification.hasLoadingSettingsError,
    isUpdating: state.notification.isUpdatingSettings
  })
)(NotificationPageFn)
